import { useState } from 'react';
import { Button, Modal, ModalFooter, ModalCloseButton, TextField, NotificationDispatch, StyledFlex, ModalTitle, FormSelect, FormErrorMessage } from '@tryhackme/thm-ui-components';
import { yupResolver } from '@hookform/resolvers/yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload } from '@fortawesome/pro-regular-svg-icons';
import { SubmitHandler, useForm } from 'src/common/form';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { UploadFile } from 'src/common/components/upload-file';
import { useUploadFileToS3Mutation } from 'src/common/slices';
import { StyledTextField } from '../add-seat-modal/add-seat-modal.styles';
import { CorpSideButton, StyledButtonIcon } from '../../corporate.styles';
import { UpdateCompanyDetailsModalProps, UpdateCompanyDetailsProps } from '../../corporate.types';
import { useLazyGetCompanyLogoPreSignedUrlQuery, useUpdateCompanyDetailsMutation } from '../../corporate.slice';
import { StyledDefaultLabel } from '../create-company-modal/create-company-modal.styles';
import { companyTypeOptions } from '../../corporate.constants';
import { updateCompanyDetailsFormValidation } from './update-company-details-modal.constants';
import { StyledCompanyLogoContainer, StyledCompanyLogoWrapper, StyledModalHeader, StyledUploadImageSpan } from './update-company-details-modal.styles';
export const UpdateCompanyDetailsModal = ({
  logo,
  companyId,
  title,
  buttonTitle,
  icon,
  updateButtonText,
  defaultValues
}: UpdateCompanyDetailsModalProps) => {
  const [lazyGetPreSignedUrl] = useLazyGetCompanyLogoPreSignedUrlQuery();
  const [uploadFileToS3] = useUploadFileToS3Mutation();
  const [updateCompanyDetails, {
    isLoading
  }] = useUpdateCompanyDetailsMutation();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [file, setFile] = useState<File | undefined>();
  const handleModalState = (state: boolean) => {
    setModalOpen(state);
    if (state) {
      reset(defaultValues);
      // eslint-disable-next-line unicorn/no-useless-undefined
      setFile(undefined);
    }
  };
  const {
    register,
    reset,
    handleSubmit,
    formState: {
      errors
    },
    watch,
    setValue
  } = useForm<UpdateCompanyDetailsProps>({
    defaultValues,
    resolver: yupResolver(updateCompanyDetailsFormValidation)
  });
  const onSubmit: SubmitHandler<UpdateCompanyDetailsModalProps['defaultValues']> = async ({
    companyName,
    url,
    type
  }) => {
    try {
      const body: UpdateCompanyDetailsProps = {
        companyId,
        companyName,
        type
      };
      if (url !== defaultValues.url) {
        body.url = url;
      }
      if (file) {
        const filename = file.name;
        const {
          url: preSignedUrl,
          filename: s3Filename
        } = await lazyGetPreSignedUrl({
          filename,
          companyId
        }).unwrap();
        await uploadFileToS3({
          file,
          url: preSignedUrl
        }).unwrap();
        body.filename = s3Filename;
      }
      await updateCompanyDetails(body).unwrap();
      NotificationDispatch('success', 'Company details were updated successfully');
    } catch (error) {
      NotificationDispatch('error', isQueryErrorWithMessage(error) ? error.data.message : 'An unknown error has occurred');
    }
    handleModalState(false);
  };
  return <Modal triggerElement={<CorpSideButton color="secondary">
          <StyledButtonIcon icon={icon} size="lg" />
          {buttonTitle}
        </CorpSideButton>} open={modalOpen} onOpenChange={handleModalState} padding="3rem" preventOnOpenAutoFocus isSmall data-sentry-element="Modal" data-sentry-component="UpdateCompanyDetailsModal" data-sentry-source-file="update-company-details-modal.tsx">
      <StyledModalHeader data-sentry-element="StyledModalHeader" data-sentry-source-file="update-company-details-modal.tsx">
        <ModalTitle data-sentry-element="ModalTitle" data-sentry-source-file="update-company-details-modal.tsx">{title}</ModalTitle>
      </StyledModalHeader>
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form id="update-company-name" method="dialog" onSubmit={handleSubmit(onSubmit)}>
        <StyledCompanyLogoContainer data-sentry-element="StyledCompanyLogoContainer" data-sentry-source-file="update-company-details-modal.tsx">
          <StyledCompanyLogoWrapper data-testid="company-image" src={file ? URL.createObjectURL(file) : logo} alt={`${defaultValues.companyName} logo`} data-sentry-element="StyledCompanyLogoWrapper" data-sentry-source-file="update-company-details-modal.tsx" />
          <StyledFlex flexDirection="column" alignItems="flex-start" alignContent="flex-start" gap="1rem" data-sentry-element="StyledFlex" data-sentry-source-file="update-company-details-modal.tsx">
            <UploadFile handleFileUpload={files => {
            setFile(files[0]);
          }} data-sentry-element="UploadFile" data-sentry-source-file="update-company-details-modal.tsx">
              <FontAwesomeIcon icon={faUpload} height="1.2rem" width="1.2rem" data-sentry-element="FontAwesomeIcon" data-sentry-source-file="update-company-details-modal.tsx" />
              Upload logo
            </UploadFile>
            <StyledUploadImageSpan data-sentry-element="StyledUploadImageSpan" data-sentry-source-file="update-company-details-modal.tsx">Max size 2mb. Formats: JPG, GIF, PNG.</StyledUploadImageSpan>
          </StyledFlex>
        </StyledCompanyLogoContainer>
        <StyledTextField data-sentry-element="StyledTextField" data-sentry-source-file="update-company-details-modal.tsx">
          <TextField {...register('companyName')} label="Company name" id="companyName-id" variant="input" type="text" isError={!!errors.companyName?.message} errorMessage={errors.companyName?.message} data-sentry-element="TextField" data-sentry-source-file="update-company-details-modal.tsx" />
        </StyledTextField>
        <StyledTextField data-sentry-element="StyledTextField" data-sentry-source-file="update-company-details-modal.tsx">
          <TextField {...register('url')} label="Company website" id="companyWebsite-id" variant="input" type="text" isError={!!errors.url?.message} errorMessage={errors.url?.message} autoComplete="off" data-sentry-element="TextField" data-sentry-source-file="update-company-details-modal.tsx" />
        </StyledTextField>
        <StyledTextField id="company-type-select" data-sentry-element="StyledTextField" data-sentry-source-file="update-company-details-modal.tsx">
          <StyledDefaultLabel data-sentry-element="StyledDefaultLabel" data-sentry-source-file="update-company-details-modal.tsx">Company type</StyledDefaultLabel>
          <FormSelect options={companyTypeOptions} selected={companyTypeOptions.find(option => option.value === watch('type'))} placeholder="Select company type" showCheckboxes={false} isSearchable={false} isError={!!errors.type} id="type" closeMenuOnSelect required handleSelection={option => setValue('type', option.value)} data-sentry-element="FormSelect" data-sentry-source-file="update-company-details-modal.tsx" />
          <FormErrorMessage role="alert" data-sentry-element="FormErrorMessage" data-sentry-source-file="update-company-details-modal.tsx">{errors.type?.message}</FormErrorMessage>
        </StyledTextField>
        <ModalFooter data-sentry-element="ModalFooter" data-sentry-source-file="update-company-details-modal.tsx">
          <ModalCloseButton text="Cancel" handleCloseButtonAction={() => {
          reset();
          // eslint-disable-next-line unicorn/no-useless-undefined
          setFile(undefined);
        }} data-sentry-element="ModalCloseButton" data-sentry-source-file="update-company-details-modal.tsx" />
          <Button type="submit" color="primary" variant="solid" disabled={isLoading} data-sentry-element="Button" data-sentry-source-file="update-company-details-modal.tsx">
            {updateButtonText}
          </Button>
        </ModalFooter>
      </form>
    </Modal>;
};